import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Sparkles, FileText, Loader2, Building2, AlertCircle, Search, ChevronRight,
  Filter, Phone, Mail, Globe, Headphones, Box, Download, X, Send, ExternalLink, Tag,
  Star, Clock, Trash2, Lock, ShieldCheck, ImageIcon, ArrowLeft,
} from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useChat } from "../context/ChatContext";
import { PageThumbnail, PagedMatches } from "./Preventivatore";
import PhotoSearchModal from "../components/PhotoSearchModal";
import { OutletMatchCard } from "./Suppliers";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Contatto consulente unico (Italian Mood) — fisso per tutti i fornitori.
const CONSULENTE_PHONE = "0735 593839";
const CONSULENTE_EMAIL = "info@italian-mood.it";

// Estrazione colore dalla query (IT + EN) per scopare la ricerca foto AI.
const COLOR_KEYWORDS = [
  "rosso", "rossa", "blu", "verde", "giallo", "gialla", "nero", "nera",
  "bianco", "bianca", "beige", "grigio", "grigia", "marrone", "rosa",
  "viola", "arancione", "tortora", "panna", "avorio", "ottone", "oro",
  "argento", "noce", "rovere", "cromato", "antracite", "sabbia", "tabacco",
  "bordeaux", "ocra", "salvia",
  "red", "blue", "green", "yellow", "black", "white", "grey", "gray", "brown",
];
function extractColorFromQuery(q) {
  if (!q) return "";
  const tokens = q.toLowerCase().split(/[\s,;]+/).filter(Boolean);
  for (const tk of tokens) if (COLOR_KEYWORDS.includes(tk)) return tk;
  return "";
}

// Schermata di gate quando l'utente non ha (ancora) l'autorizzazione a Magic Search.
function MagicAccessGate({ status, onRequest, busy }) {
  const map = {
    none: { title: "Autorizzazione richiesta", desc: "Per usare Magic Search devi prima richiedere l'autorizzazione all'amministratore.", cta: "Richiedi autorizzazione", showBtn: true },
    pending: { title: "Richiesta in attesa di approvazione", desc: "La tua richiesta è stata inviata. Un amministratore la valuterà a breve: riceverai una email all'approvazione.", cta: "In attesa di approvazione", showBtn: false },
    rejected: { title: "Richiesta non approvata", desc: "La tua richiesta non è stata approvata. Puoi inviarne una nuova.", cta: "Invia nuova richiesta", showBtn: true },
    revoked: { title: "Accesso revocato", desc: "L'accesso a Magic Search è stato revocato. Puoi richiederlo di nuovo.", cta: "Richiedi di nuovo", showBtn: true },
    expired: { title: "Autorizzazione scaduta", desc: "L'autorizzazione è scaduta dopo 30 giorni di inutilizzo. Richiedila di nuovo per continuare.", cta: "Richiedi di nuovo", showBtn: true },
  };
  const cfg = map[status] || map.none;
  return (
    <div className="mt-10 bg-white border border-ink/10 rounded-sm p-10 text-center shadow-sm" data-testid="magic-access-gate" data-status={status}>
      <div className="w-14 h-14 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center mx-auto mb-5">
        {status === "pending" ? <Clock size={22} className="text-amber-600" /> : <Lock size={22} className="text-gold" />}
      </div>
      <h2 className="font-display font-light text-2xl text-ink mb-2">{cfg.title}</h2>
      <p className="text-slate1 text-sm max-w-md mx-auto leading-relaxed">{cfg.desc}</p>
      {cfg.showBtn ? (
        <button
          onClick={onRequest}
          disabled={busy}
          className="mt-6 inline-flex items-center gap-2 bg-ink text-cream px-6 py-2.5 text-xs tracking-[0.2em] uppercase rounded-sm hover:bg-ink/90 disabled:opacity-50 transition-colors"
          data-testid="magic-access-request-btn"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />} {cfg.cta}
        </button>
      ) : (
        <div className="mt-6 inline-flex items-center gap-2 border border-amber-300 bg-amber-50 text-amber-800 px-6 py-2.5 text-xs tracking-[0.2em] uppercase rounded-sm" data-testid="magic-access-pending-badge">
          <Clock size={14} /> {cfg.cta}
        </div>
      )}
    </div>
  );
}

const formatWebsite = (w) => {
  if (!w) return "";
  return w.startsWith("http://") || w.startsWith("https://") ? w : `https://${w}`;
};

export default function MagicSearchPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { setOpen: setChatOpen } = useChat();

  const [query, setQuery] = useState("");
  const [loadingAI, setLoadingAI] = useState(false);
  const [loadingTrad, setLoadingTrad] = useState(false);
  const [aiResp, setAiResp] = useState(null);
  const [tradResp, setTradResp] = useState(null);
  const [error, setError] = useState("");
  const [brandContacts, setBrandContacts] = useState(null);
  // Drawer 3D: null oppure { supplierId, supplierName }
  const [drawer, setDrawer] = useState(null);
  // Preferiti + storico ricerche
  const [favorites, setFavorites] = useState([]);
  const [favOpen, setFavOpen] = useState(false);
  const [history, setHistory] = useState([]);
  // Autorizzazione Magic Search (gate)
  const [access, setAccess] = useState(null);
  const [accessBusy, setAccessBusy] = useState(false);
  // Foto prodotto AI (come Preventivatore): strip in alto + modale
  const [photoModal, setPhotoModal] = useState(null);
  const [topPhotos, setTopPhotos] = useState({ loading: false, items: [], error: "" });

  const favMap = useMemo(() => {
    const m = new Map();
    for (const f of favorites) m.set(`${f.asset_id}:${f.page}`, f);
    return m;
  }, [favorites]);

  const eligible = user && user.role !== "admin" && ["trader", "studio"].includes(user.account_type);

  const fetchFavorites = useCallback(async () => {
    try { const { data } = await api.get("/magic-search/favorites"); setFavorites(data.favorites || []); }
    catch { /* silent */ }
  }, []);
  const fetchHistory = useCallback(async () => {
    try { const { data } = await api.get("/magic-search/history"); setHistory(data.history || []); }
    catch { /* silent */ }
  }, []);

  const fetchAccess = useCallback(async () => {
    try { const { data } = await api.get("/magic-search/access"); setAccess(data); }
    catch { setAccess({ status: "none" }); }
  }, []);

  useEffect(() => {
    if (!eligible) return;
    fetchAccess();
    fetchFavorites();
    fetchHistory();
  }, [eligible, fetchAccess, fetchFavorites, fetchHistory]);

  const requestAccess = useCallback(async () => {
    setAccessBusy(true);
    try {
      const { data } = await api.post("/magic-search/access/request");
      toast.success(data.status === "approved" ? "Accesso già attivo" : "Richiesta inviata all'amministratore");
      await fetchAccess();
    } catch (e) { toast.error(e.response?.data?.detail || "Richiesta non riuscita"); }
    finally { setAccessBusy(false); }
  }, [fetchAccess]);

  const toggleFavorite = useCallback(async (result, page) => {
    const key = `${result.asset_id}:${page}`;
    const existing = favMap.get(key);
    try {
      if (existing) {
        await api.delete(`/magic-search/favorites/${existing.id}`);
        setFavorites((prev) => prev.filter((f) => f.id !== existing.id));
      } else {
        const { data } = await api.post("/magic-search/favorites", {
          asset_id: result.asset_id,
          supplier_id: result.supplier_id || "",
          supplier_name: result.supplier_name || "",
          kind: result.kind || "",
          page,
          title: result.title || result.filename || "",
          query: query.trim(),
        });
        setFavorites((prev) => (prev.some((f) => f.id === data.id) ? prev : [data, ...prev]));
      }
    } catch { toast.error("Operazione non riuscita"); }
  }, [favMap, query]);

  const deleteHistory = useCallback(async (id) => {
    try { await api.delete(`/magic-search/history/${id}`); setHistory((prev) => prev.filter((h) => h.id !== id)); }
    catch { /* silent */ }
  }, []);

  // Guard: solo Trader/Studio. Admin → Preventivatore. Altri → Fornitori.
  useEffect(() => {
    if (!user) return;
    if (user.role === "admin") { navigate("/preventivatore", { replace: true }); return; }
    if (!["trader", "studio"].includes(user.account_type)) {
      navigate("/fornitori", { replace: true });
    }
  }, [user, navigate]);

  const runSearch = useCallback(async (qArg) => {
    const raw = typeof qArg === "string" ? qArg : query;
    const q = (raw || "").trim();
    if (q.length < 2) { setError("Inserisci almeno 2 caratteri"); return; }
    if (typeof qArg === "string") setQuery(qArg);
    setError("");
    setAiResp(null); setTradResp(null);
    setLoadingAI(true); setLoadingTrad(true);
    setTopPhotos({ loading: true, items: [], error: "" });
    const autoColor = extractColorFromQuery(q);
    const aiPromise = api.post("/magic-search/search", { q })
      .then(({ data }) => { setAiResp(data); return data; })
      .catch((e) => { if (e.response?.status === 403) fetchAccess(); toast.error(`AI: ${e.response?.data?.detail || e.message || "Errore"}`); return null; })
      .finally(() => setLoadingAI(false));
    const tradPromise = api.post("/magic-search/traditional-search", { q })
      .then(({ data }) => { setTradResp(data); return data; })
      .catch((e) => { if (e.response?.status === 403) fetchAccess(); toast.error(`Ricerca: ${e.response?.data?.detail || e.message || "Errore"}`); return null; })
      .finally(() => setLoadingTrad(false));
    // La ricerca foto parte SOLO sul sito ufficiale del fornitore rilevato
    // (fallback al web solo se il sito non offre almeno 3 immagini).
    const [aiData, tradData] = await Promise.all([aiPromise, tradPromise]);
    const detectedSupplierId = aiData?.brand_filter?.id || tradData?.brand_filter?.id || "";
    try {
      const { data } = await api.post("/preventivatore/photo-search", { product_name: q, supplier_id: detectedSupplierId, color: autoColor });
      setTopPhotos({ loading: false, items: data.items || [], error: "" });
    } catch (e) {
      setTopPhotos({ loading: false, items: [], error: e.response?.data?.detail || e.message || "Errore foto" });
    }
    fetchHistory();
  }, [query, fetchHistory, fetchAccess]);

  const openPhotoSearch = useCallback((result) => {
    const autoColor = extractColorFromQuery(query);
    const tokensClean = (aiResp?.tokens || []).join(" ").trim();
    setPhotoModal({
      productName: tokensClean || query.trim(),
      supplierId: result?.supplier_id || "",
      supplierName: result?.supplier_name || "",
      color: autoColor,
    });
  }, [query, aiResp]);

  const openTopPhoto = useCallback((idx) => {
    setPhotoModal({
      productName: query.trim(),
      supplierId: "",
      supplierName: "Ricerca generica",
      color: extractColorFromQuery(query),
      initialIdx: idx,
      initialResults: topPhotos.items,
    });
  }, [query, topPhotos.items]);

  const openPdf = useCallback((assetId, page, tokens) => {
    const fileUrl = `${API}/assets/${assetId}/preview`;
    const qp = new URLSearchParams();
    qp.set("file", fileUrl);
    if (page && Number.isFinite(Number(page))) qp.set("page", String(page));
    const hl = (tokens && tokens[0]) || (query.trim().split(/\s+/)[0] || "").toLowerCase();
    if (hl) qp.set("search", hl);
    qp.set("from", "magic-search");
    window.open(`/pdfjs/viewer.html?${qp.toString()}`, "_blank");
  }, [query]);

  const requestConditions = useCallback(async (supplierId, supplierName) => {
    try {
      const { data } = await api.post(`/suppliers/${supplierId}/discount-request`);
      if (data.status === "already_set") toast.success(`Condizioni già attive su ${supplierName}`);
      else if (data.status === "pending") toast.info(`Richiesta già inviata per ${supplierName}`);
      else toast.success(`Richiesta condizioni inviata per ${supplierName}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Richiesta non riuscita");
    }
  }, []);

  const loading = loadingAI || loadingTrad;
  const brandFilter = aiResp?.brand_filter || tradResp?.brand_filter;
  const brandId = brandFilter?.id || null;

  useEffect(() => {
    if (!brandId) { setBrandContacts(null); return undefined; }
    let cancelled = false;
    api.get(`/suppliers/${brandId}`)
      .then((r) => { if (!cancelled) setBrandContacts(r.data); })
      .catch(() => { if (!cancelled) setBrandContacts(null); });
    return () => { cancelled = true; };
  }, [brandId]);

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); runSearch(); }
  };

  // Gate autorizzazione: se l'utente non è approvato, mostra la schermata di richiesta.
  if (eligible && access === null) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-28 pb-10 overflow-x-clip" data-testid="magic-search-page">
        <div className="flex items-center justify-center py-24" data-testid="magic-access-loading">
          <Loader2 size={28} className="animate-spin text-slate1" />
        </div>
      </div>
    );
  }

  if (eligible && access && access.status !== "approved") {
    return (
      <div className="max-w-[900px] mx-auto px-6 lg:px-10 pt-28 pb-10" data-testid="magic-search-page">
        <div className="mb-2 text-[11px] tracking-[0.3em] uppercase text-gold inline-flex items-center gap-2">
          <Sparkles size={12} /> Ricerca intelligente listini
        </div>
        <h1 className="font-display font-light text-4xl lg:text-5xl text-ink tracking-tight">Magic Search</h1>
        <MagicAccessGate status={access.status} onRequest={requestAccess} busy={accessBusy} />
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-28 pb-10" data-testid="magic-search-page">
      {/* Header */}
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] tracking-[0.3em] uppercase text-gold mb-2 inline-flex items-center gap-2">
            <Sparkles size={12} /> Ricerca intelligente listini
          </div>
          <h1 className="font-display font-light text-4xl lg:text-5xl text-ink tracking-tight">Magic Search</h1>
          <p className="mt-3 text-slate1 text-base max-w-2xl leading-relaxed">
            Cerca in tutti i listini dei fornitori partner. A sinistra l&apos;AI interpreta
            la tua richiesta e propone i risultati più pertinenti; a destra la ricerca
            esatta ti dà tutti i match parola per parola.
          </p>
        </div>
        <div className="shrink-0 flex items-center gap-2 self-start sm:self-auto">
          <Link
            to="/fornitori"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-sm border border-ink/20 bg-white text-ink text-xs tracking-[0.15em] uppercase hover:border-gold hover:text-ink transition-colors"
            data-testid="magic-back-to-suppliers"
          >
            <ArrowLeft size={15} className="text-slate1" /> Torna ai listini
          </Link>
          <button
            onClick={() => setFavOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-sm border border-ink/20 bg-white text-ink text-xs tracking-[0.15em] uppercase hover:border-gold hover:text-ink transition-colors"
            data-testid="magic-favorites-open"
          >
            <Star size={15} className={favorites.length ? "text-gold fill-gold" : "text-slate1"} />
            Preferiti{favorites.length ? ` (${favorites.length})` : ""}
          </button>
        </div>
      </div>

      {/* Search bar */}
      <div className="bg-white border border-ink/10 rounded-sm p-5 shadow-sm">
        <div className="flex items-center gap-3">
          <Search size={18} className="text-slate1 shrink-0" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Es: tavolo allungabile cristallo, poltrona cocoon…"
            maxLength={200}
            disabled={loading}
            className="flex-1 min-w-0 bg-transparent border-0 focus:outline-none text-base text-ink placeholder:text-slate1/50"
            data-testid="magic-search-query-input"
            autoFocus
          />
          <button
            onClick={() => runSearch()}
            disabled={loading || query.trim().length < 2}
            className="inline-flex items-center gap-2 bg-ink text-cream px-5 py-2 text-xs tracking-[0.2em] uppercase rounded-sm hover:bg-ink/90 disabled:opacity-50 transition-colors"
            data-testid="magic-search-run-btn"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {loading ? "Ricerca…" : "Cerca"}
          </button>
        </div>
      </div>

      {/* Storico ricerche */}
      {history.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="magic-history">
          <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase text-slate1">
            <Clock size={11} /> Recenti
          </span>
          {history.map((h) => (
            <span key={h.id} className="group inline-flex items-center gap-1 bg-white border border-ink/15 rounded-full pl-3 pr-1 py-1 text-xs text-ink hover:border-gold transition-colors" data-testid="magic-history-chip">
              <button
                onClick={() => runSearch(h.q)}
                disabled={loading}
                className="hover:text-gold transition-colors disabled:opacity-50"
                title="Rilancia questa ricerca"
              >
                {h.q}
              </button>
              <button
                onClick={() => deleteHistory(h.id)}
                className="p-0.5 text-slate1/50 hover:text-rose-600 transition-colors"
                title="Rimuovi dallo storico"
                data-testid="magic-history-remove"
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}

      {error && (
        <div className="mt-4 bg-rose-50 border border-rose-300 text-rose-800 p-3 rounded-sm text-sm flex items-start gap-2" data-testid="magic-search-error">
          <AlertCircle size={14} className="shrink-0 mt-0.5" /> {error}
        </div>
      )}

      {/* Brand row: consulente (C) + sito + File 3D (E) */}
      {brandFilter && (
        <div className="mt-6 flex flex-wrap items-stretch gap-3" data-testid="magic-brand-row">
          <div className="bg-ink/95 text-cream rounded-sm p-3 inline-flex items-center gap-2 text-xs self-start">
            <Building2 size={13} className="text-gold" />
            <span className="text-[10px] tracking-[0.25em] uppercase text-gold">Fornitore</span>
            <span className="text-cream font-medium">{brandFilter.name}</span>
          </div>
          <div className="bg-white border border-ink/10 rounded-sm px-4 py-2.5 flex flex-wrap items-center gap-x-6 gap-y-1.5 text-xs flex-1 min-w-[280px]" data-testid="magic-consulente">
            <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-gold font-semibold">
              <Headphones size={12} /> Consulente
            </span>
            <a href={`tel:${CONSULENTE_PHONE.replace(/\s/g, "")}`} className="inline-flex items-center gap-1 text-slate1 hover:text-ink transition-colors" data-testid="magic-consulente-phone">
              <Phone size={11} /> {CONSULENTE_PHONE}
            </a>
            <a
              href={`mailto:${CONSULENTE_EMAIL}?subject=${encodeURIComponent(`Richiesta ${brandFilter.name}`)}`}
              className="inline-flex items-center gap-1 text-slate1 hover:text-ink transition-colors"
              data-testid="magic-consulente-email"
            >
              <Mail size={11} /> {CONSULENTE_EMAIL}
            </a>
            {brandContacts?.website && (
              <a href={formatWebsite(brandContacts.website)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-slate1 hover:text-ink transition-colors" data-testid="magic-supplier-website">
                <Globe size={11} /> Sito fornitore
              </a>
            )}
            <button
              onClick={() => setDrawer({ supplierId: brandId, supplierName: brandFilter.name })}
              className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 bg-gold/15 border border-gold/50 text-ink rounded-sm text-[10px] tracking-[0.2em] uppercase hover:bg-gold/25 transition-colors font-medium"
              data-testid="magic-3d-open"
            >
              <Box size={12} /> File 3D
            </button>
          </div>
        </div>
      )}

      {/* Foto prodotto AI · colpo d'occhio (come Preventivatore) */}
      {(topPhotos.loading || topPhotos.items.length > 0) && (
        <div className="mt-6 bg-white border border-ink/10 rounded-sm p-4" data-testid="magic-photo-strip">
          <div className="flex items-center gap-2 mb-3">
            <ImageIcon size={13} className="text-gold" />
            <span className="text-[11px] tracking-[0.25em] uppercase text-slate1">Foto prodotto · colpo d&apos;occhio</span>
            {topPhotos.loading && (
              <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase text-slate1/70">
                <Loader2 size={10} className="animate-spin" /> ricerca in corso
              </span>
            )}
            {!topPhotos.loading && topPhotos.items.length > 0 && (
              <button
                onClick={() => openTopPhoto(0)}
                className="ml-auto inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-gold hover:text-ink transition-colors"
                data-testid="magic-photo-strip-expand"
              >
                Espandi →
              </button>
            )}
          </div>
          {topPhotos.loading ? (
            <div className="grid grid-cols-3 gap-3">
              {[0, 1, 2].map((i) => (<div key={i} className="aspect-[4/3] bg-cream2 animate-pulse rounded-sm" />))}
            </div>
          ) : topPhotos.items.length > 0 ? (
            <div className="grid grid-cols-3 gap-3">
              {topPhotos.items.slice(0, 3).map((it, i) => (
                <button
                  key={`${it.url}-${i}`}
                  type="button"
                  onClick={() => openTopPhoto(i)}
                  className="group relative aspect-[4/3] bg-cream2 border border-ink/10 rounded-sm overflow-hidden hover:border-gold transition-colors"
                  title={`${it.title || "Foto " + (i + 1)} · click per ingrandire`}
                  data-testid={`magic-photo-strip-thumb-${i}`}
                >
                  <img
                    src={it.thumbnail}
                    alt={it.title || `Foto ${i + 1}`}
                    className="w-full h-full object-contain transition-transform group-hover:scale-105"
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    onError={(e) => { if (it.url && e.target.src !== it.url) e.target.src = it.url; }}
                  />
                  <div className="absolute top-1.5 left-1.5 bg-ink/80 text-cream rounded-sm px-1.5 py-0.5 text-[9px] tracking-[0.15em] uppercase backdrop-blur-sm">#{i + 1}</div>
                </button>
              ))}
            </div>
          ) : null}
        </div>
      )}

      {/* Colonne risultati */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ResultColumn
          variant="ai"
          loading={loadingAI}
          resp={aiResp}
          favMap={favMap}
          onToggleFav={toggleFavorite}
          onOpen={(r, page) => openPdf(r.asset_id, page ?? r.suggested_page, aiResp?.tokens)}
          onRequest={requestConditions}
          on3D={(r) => setDrawer({ supplierId: r.supplier_id, supplierName: r.supplier_name })}
          onPhotoSearch={openPhotoSearch}
        />
        <ResultColumn
          variant="trad"
          loading={loadingTrad}
          resp={tradResp}
          favMap={favMap}
          onToggleFav={toggleFavorite}
          onOpen={(r, page) => openPdf(r.asset_id, page, tradResp?.tokens)}
          onRequest={requestConditions}
          on3D={(r) => setDrawer({ supplierId: r.supplier_id, supplierName: r.supplier_name })}
          onPhotoSearch={openPhotoSearch}
        />
      </div>

      {/* Outlet stesso marchio (come nelle schede dei listini) */}
      {brandId && (
        <div className="mt-6" data-testid="magic-outlet-match">
          <OutletMatchCard supplierId={brandId} />
        </div>
      )}

      {drawer && (
        <Models3DDrawer
          supplierId={drawer.supplierId}
          supplierName={drawer.supplierName}
          fallbackQuery={query}
          onAskChat={setChatOpen}
          onClose={() => setDrawer(null)}
        />
      )}

      {favOpen && (
        <FavoritesDrawer
          favorites={favorites}
          onOpen={(f) => openPdf(f.asset_id, f.page)}
          onRemove={async (f) => {
            try { await api.delete(`/magic-search/favorites/${f.id}`); setFavorites((prev) => prev.filter((x) => x.id !== f.id)); }
            catch { toast.error("Rimozione non riuscita"); }
          }}
          onClose={() => setFavOpen(false)}
        />
      )}

      {/* Modale foto prodotto AI (reject disabilitato per gli utenti) */}
      <PhotoSearchModal
        open={!!photoModal}
        productName={photoModal?.productName || ""}
        supplierId={photoModal?.supplierId || ""}
        supplierName={photoModal?.supplierName || ""}
        color={photoModal?.color || ""}
        initialIdx={typeof photoModal?.initialIdx === "number" ? photoModal.initialIdx : -1}
        initialResults={photoModal?.initialResults || null}
        canReject={false}
        onClose={() => setPhotoModal(null)}
      />
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Badge termini utente (D): sconto riservato / condizioni non attive · aumento · consegna
// ───────────────────────────────────────────────────────────────────────────
function UserTermsBadges({ result, onRequest }) {
  const hasIncrease = !!(result.applies_price_increase && (result.price_increase_pct > 0 || result.price_increase_new_listino));
  const lead = Number(result.supplier_lead_time_days || 0);
  const formatDate = (iso) => {
    if (!iso) return "";
    const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}/${m[2]}/${m[1].slice(2)}` : iso;
  };
  return (
    <div className="flex flex-col items-end gap-1 shrink-0">
      {result.user_has_access && result.user_discount_label ? (
        <div className="bg-gold text-ink px-2 py-1 rounded-sm border border-gold leading-none" data-testid="magic-user-discount">
          <div className="text-[7px] tracking-[0.25em] uppercase opacity-70">Tuo sconto</div>
          <div className="text-xs font-semibold mt-0.5">−{result.user_discount_label}</div>
        </div>
      ) : (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRequest(result.supplier_id, result.supplier_name); }}
          className="bg-white text-slate1 border border-ink/20 px-2 py-1 rounded-sm leading-none hover:border-gold hover:text-ink transition-colors text-right"
          title="Richiedi le condizioni riservate per questo fornitore"
          data-testid="magic-request-conditions"
        >
          <div className="text-[7px] tracking-[0.2em] uppercase opacity-70">Condizioni non attive</div>
          <div className="text-[10px] font-semibold mt-0.5 inline-flex items-center gap-1"><Tag size={9} /> Richiedi</div>
        </button>
      )}
      {hasIncrease && (
        <div className="bg-rose-900/90 text-cream px-2 py-1 rounded-sm border border-rose-700/70 leading-none" data-testid="magic-increase-badge"
          title={result.price_increase_date ? `In vigore dal ${formatDate(result.price_increase_date)}` : "Aumento listino programmato"}>
          <div className="text-[7px] tracking-[0.25em] uppercase opacity-70">
            {result.price_increase_date ? `Dal ${formatDate(result.price_increase_date)}` : "Aumento"}
          </div>
          <div className="text-xs font-semibold mt-0.5">
            {result.price_increase_new_listino ? "Nuovo listino" : `+${Number(result.price_increase_pct).toFixed(result.price_increase_pct % 1 === 0 ? 0 : 1)}%`}
          </div>
        </div>
      )}
      {lead > 0 && (
        <div className="bg-cream2 text-ink px-2 py-1 rounded-sm border border-ink/10 leading-none" data-testid="magic-lead-badge" title="Termini di consegna indicativi">
          <div className="text-[7px] tracking-[0.25em] uppercase opacity-60">Consegna</div>
          <div className="text-xs font-semibold mt-0.5">~{lead} gg</div>
        </div>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Colonna risultati (gestisce sia AI che Tradizionale)
// ───────────────────────────────────────────────────────────────────────────
function ResultColumn({ variant, loading, resp, onOpen, onRequest, on3D, favMap, onToggleFav, onPhotoSearch }) {
  const isAI = variant === "ai";
  const results = resp?.results || [];
  return (
    <div className={`min-w-0 bg-white rounded-sm overflow-hidden border ${isAI ? "border-gold/40" : "border-ink/20"}`} data-testid={`magic-col-${variant}`}>
      <div className={`px-4 py-3 border-b ${isAI ? "bg-gold/10 border-gold/30" : "bg-cream2/50 border-ink/15"}`}>
        <div className="flex items-center justify-between">
          <div className="inline-flex items-center gap-2">
            {isAI ? <Sparkles size={14} className="text-gold" /> : <Filter size={14} className="text-ink" />}
            <span className="text-[11px] tracking-[0.25em] uppercase text-ink font-medium">
              {isAI ? "Risultati consigliati" : "Ricerca esatta"}
            </span>
          </div>
          {isAI
            ? (resp?.candidates_count !== undefined && <span className="text-[10px] tracking-[0.15em] uppercase text-slate1">Top {results.length}</span>)
            : (resp?.total_matches !== undefined && <span className="text-[10px] tracking-[0.15em] uppercase text-slate1">{results.length} file · {resp.total_matches} match</span>)}
        </div>
      </div>

      <div className="p-4">
        {loading && (
          <div className="flex items-center justify-center gap-2 py-8 text-slate1 text-xs">
            <Loader2 size={14} className="animate-spin" /> {isAI ? "L'AI sta cercando…" : "Ricerca in corso…"}
          </div>
        )}

        {!loading && isAI && resp?.ai_reasoning && (
          <div className="bg-gold/5 border border-gold/30 rounded-sm p-3 mb-4" data-testid="magic-ai-reasoning">
            <div className="text-[10px] tracking-[0.2em] uppercase text-gold mb-2 inline-flex items-center gap-1">
              <Sparkles size={11} /> Interpretazione
            </div>
            <div className="text-xs text-ink leading-relaxed italic">{resp.ai_reasoning}</div>
          </div>
        )}

        {!loading && results.length > 0 && (
          <div className="space-y-2" data-testid={`magic-results-${variant}`}>
            {results.map((r, i) => {
              const pages = isAI ? (r.snippets || []) : (r.matches || []);
              const repPage = isAI ? (r.suggested_page || pages[0]?.page || 1) : (pages[0]?.page || 1);
              const isFav = favMap?.has(`${r.asset_id}:${repPage}`);
              return (
                <div key={r.asset_id} className="bg-cream/30 border border-ink/10 rounded-sm p-3" data-testid={`magic-${variant}-result-${i}`}>
                  <div className="flex items-start justify-between gap-3 mb-1">
                    <div className="flex items-center gap-2 text-[9px] tracking-[0.2em] uppercase text-slate1 min-w-0">
                      {isAI && <span className="font-display text-lg text-gold leading-none">#{i + 1}</span>}
                      <Building2 size={10} />
                      <span className="truncate">{r.supplier_name || "—"}</span>
                      <span className="text-ink/30">·</span>
                      <span className="text-gold">{r.kind}</span>
                      {isAI && r.suggested_page && (<><span className="text-ink/30">·</span><span className="font-medium">Pag. {r.suggested_page}</span></>)}
                      {!isAI && <><span className="text-ink/30">·</span><span>{r.matches_count} match</span></>}
                    </div>
                    <UserTermsBadges result={r} onRequest={onRequest} />
                  </div>

                  <div className="flex items-center gap-2 mb-2">
                    <button
                      type="button"
                      onClick={() => onOpen(r, isAI ? r.suggested_page : (pages[0]?.page ?? null))}
                      className="flex items-start gap-2 text-left flex-1 min-w-0 hover:text-gold transition-colors"
                      data-testid={`magic-${variant}-result-${i}-open`}
                    >
                      <FileText size={12} className="text-slate1 shrink-0 mt-0.5" />
                      <div className="text-xs font-medium text-ink leading-snug">{r.title || r.filename}</div>
                      <ChevronRight size={14} className="text-slate1 shrink-0 mt-0.5 ml-auto" />
                    </button>
                    <button
                      type="button"
                      onClick={() => onToggleFav(r, repPage)}
                      className={`shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-sm border transition-colors ${isFav ? "border-gold bg-gold/15 text-gold" : "border-ink/15 text-slate1 hover:border-gold hover:text-gold"}`}
                      title={isFav ? "Rimuovi dai preferiti" : "Salva nei preferiti"}
                      data-testid={`magic-${variant}-result-${i}-fav`}
                    >
                      <Star size={13} className={isFav ? "fill-gold" : ""} />
                    </button>
                    <button
                      type="button"
                      onClick={() => on3D(r)}
                      className="shrink-0 inline-flex items-center gap-1 px-2 py-1 text-[10px] tracking-[0.15em] uppercase border border-ink/15 text-slate1 hover:border-gold hover:text-ink rounded-sm transition-colors"
                      title="File 3D / CAD di questo fornitore"
                      data-testid={`magic-${variant}-result-${i}-3d`}
                    >
                      <Box size={11} /> 3D
                    </button>
                    <button
                      type="button"
                      onClick={() => onPhotoSearch(r)}
                      className="shrink-0 inline-flex items-center gap-1 px-2 py-1 text-[10px] tracking-[0.15em] uppercase border border-gold/40 text-gold hover:bg-gold/10 rounded-sm transition-colors"
                      title="Cerca foto prodotto con AI sul sito del fornitore"
                      data-testid={`magic-${variant}-result-${i}-photo-ai`}
                    >
                      <ImageIcon size={11} /> Foto AI
                    </button>
                  </div>

                  {pages.length > 0 && (
                    <PagedMatches
                      items={pages}
                      wrapperClassName="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 min-w-0"
                      testidPrefix={`magic-${variant}-result-${i}`}
                      renderItem={(s) => {
                        const pFav = favMap?.has(`${r.asset_id}:${s.page}`);
                        return (
                          <div key={`${r.asset_id}-${s.page}`} className="relative shrink-0">
                            <PageThumbnail
                              assetId={r.asset_id}
                              page={s.page}
                              pageLabel
                              snippet={s.snippet}
                              suggested={isAI && s.page === r.suggested_page}
                              onClick={() => onOpen(r, s.page)}
                            />
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); onToggleFav(r, s.page); }}
                              className={`absolute top-1 right-1 z-10 inline-flex items-center justify-center w-6 h-6 rounded-sm border shadow-sm transition-colors ${pFav ? "border-gold bg-gold text-white" : "border-white/70 bg-ink/55 text-white hover:bg-gold hover:border-gold"}`}
                              title={pFav ? "Rimuovi questa pagina dai preferiti" : "Salva questa pagina nei preferiti"}
                              data-testid={`magic-${variant}-result-${i}-page-${s.page}-fav`}
                            >
                              <Star size={12} className={pFav ? "fill-white" : ""} />
                            </button>
                          </div>
                        );
                      }}
                    />
                  )}

                  {isAI && r.ai_reason && (
                    <div className="mt-2 text-[11px] text-slate1 italic leading-relaxed line-clamp-3">
                      <span className="text-gold not-italic">↳</span> {r.ai_reason}
                    </div>
                  )}
                  {!isAI && pages[0]?.snippet && (
                    <div className="mt-2 text-[11px] text-ink/70 bg-cream2/40 px-2 py-1 rounded-sm leading-relaxed line-clamp-2">
                      {pages[0].snippet}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {!loading && resp && results.length === 0 && (
          <div className="text-center py-8 text-slate1">
            <Search size={24} className="mx-auto mb-2 text-slate1/40" />
            <div className="text-xs">Nessun risultato per &ldquo;{resp.query}&rdquo;.</div>
          </div>
        )}
        {!loading && !resp && (
          <div className="text-center text-slate1/60 text-xs py-8">Inserisci una richiesta per iniziare.</div>
        )}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Mini-anteprima file 3D: usa la miniatura renderizzata da Google Drive quando
// disponibile (molti DWG, immagini, PDF), altrimenti l'icona del tipo file,
// altrimenti il cubetto. Fallback a cascata via onError.
function Model3DThumb({ file, isPriority }) {
  const [stage, setStage] = useState(file?.thumbnail_link ? "thumb" : (file?.icon_link ? "icon" : "box"));
  const box = `shrink-0 w-11 h-11 rounded-sm border flex items-center justify-center overflow-hidden bg-cream ${isPriority ? "border-gold" : "border-ink/10"}`;
  if (stage === "thumb") {
    return (
      <div className={box} data-testid="magic-3d-thumb">
        <img src={file.thumbnail_link} alt="" referrerPolicy="no-referrer" loading="lazy"
          className="w-full h-full object-cover" onError={() => setStage(file.icon_link ? "icon" : "box")} />
      </div>
    );
  }
  if (stage === "icon") {
    return (
      <div className={box} data-testid="magic-3d-thumb">
        <img src={file.icon_link} alt="" referrerPolicy="no-referrer" loading="lazy"
          className="w-6 h-6 object-contain" onError={() => setStage("box")} />
      </div>
    );
  }
  return (
    <div className={box} data-testid="magic-3d-thumb">
      <Box size={18} className={isPriority ? "text-gold" : "text-ink/40"} />
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Drawer 3D (E): asset "cad_3d" del fornitore + richiesta modello in chat
// ───────────────────────────────────────────────────────────────────────────
export function Models3DDrawer({ supplierId, supplierName, fallbackQuery, onAskChat, onClose, allowAsk = true }) {
  const [state, setState] = useState({ loading: true, files: [] });
  const [q, setQ] = useState("");
  const [asking, setAsking] = useState(false);

  useEffect(() => {
    let alive = true;
    setState({ loading: true, files: [] });
    api.get(`/suppliers/${supplierId}/cad-3d-files`)
      .then(({ data }) => {
        if (!alive) return;
        setState({ loading: false, files: data?.files || [] });
      })
      .catch(() => { if (alive) setState({ loading: false, files: [] }); });
    return () => { alive = false; };
  }, [supplierId]);

  // Token di priorità = parole della ricerca del preventivatore, SENZA il nome
  // del fornitore (es. "poliform grace" -> ["grace"]). I file che contengono
  // questi token nel nome vengono messi in cima alla lista.
  const priorityTokens = useMemo(() => {
    const supTokens = new Set((supplierName || "").toLowerCase().split(/\s+/).filter(Boolean));
    return (fallbackQuery || "")
      .toLowerCase()
      .split(/\s+/)
      .map((t) => t.replace(/[^a-z0-9]/g, ""))
      .filter((t) => t.length >= 2 && !supTokens.has(t));
  }, [fallbackQuery, supplierName]);

  const priorityScore = (name) => {
    const n = (name || "").toLowerCase();
    return priorityTokens.reduce((acc, t) => (n.includes(t) ? acc + 1 : acc), 0);
  };

  const filtered = useMemo(() => {
    const terms = q.toLowerCase().split(/\s+/).filter(Boolean);
    const list = state.files.filter((f) => {
      if (terms.length === 0) return true;
      const hay = (f.name || "").toLowerCase();
      return terms.every((t) => hay.includes(t));
    });
    return [...list].sort((a, b) => {
      const sb = priorityScore(b.name) - priorityScore(a.name);
      if (sb !== 0) return sb;
      return (a.name || "").localeCompare(b.name || "", undefined, { numeric: true });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.files, q, priorityTokens]);

  const matchedCount = useMemo(
    () => (priorityTokens.length ? state.files.filter((f) => priorityScore(f.name) > 0).length : 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.files, priorityTokens]
  );

  const download = async (f) => {
    if (f.source === "upload") {
      try {
        const r = await api.get(`/assets/${f.asset_id}/download`, { responseType: "blob" });
        const url = URL.createObjectURL(r.data);
        const link = document.createElement("a");
        link.href = url; link.download = f.name || "file";
        document.body.appendChild(link); link.click(); link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 4000);
      } catch { toast.error("Download non riuscito"); }
      return;
    }
    const target = f.download_url || f.web_view_link;
    if (target) window.open(target, "_blank", "noopener");
    else toast.error("File non disponibile");
  };

  const askModel = async () => {
    const term = (q || fallbackQuery || "").trim();
    const text = `Richiesta modello 3D — Fornitore: ${supplierName}${term ? ` · Cerco: "${term}"` : ""}. Potete aiutarmi a trovare/scaricare questo file 3D?`;
    setAsking(true);
    try {
      const fd = new FormData();
      fd.append("body", text);
      await api.post("/chat/messages", fd);
      toast.success("Richiesta inviata in chat");
      onClose();
      onAskChat(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invio non riuscito");
    } finally { setAsking(false); }
  };

  return (
    <>
      <div className="fixed inset-0 z-[110] bg-black/40" onClick={onClose} data-testid="magic-3d-overlay" />
      <div className="fixed right-0 top-0 bottom-0 z-[111] w-[92vw] max-w-[460px] bg-cream shadow-2xl flex flex-col" data-testid="magic-3d-drawer">
        <div className="flex items-center justify-between px-5 py-4 bg-ink text-cream">
          <div className="flex items-center gap-2">
            <Box size={18} className="text-gold" />
            <div>
              <div className="text-sm font-semibold tracking-wide">File 3D / CAD</div>
              <div className="text-[10px] text-cream/60">{supplierName}</div>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-cream/10 rounded" data-testid="magic-3d-close"><X size={18} /></button>
        </div>

        <div className="p-4 border-b border-ink/10">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink/40" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cerca un modello 3D…"
              className="w-full pl-8 pr-3 py-2 text-sm rounded-sm border border-ink/15 focus:outline-none focus:border-ink/40 bg-white"
              data-testid="magic-3d-search"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="magic-3d-list">
          {state.loading && (
            <div className="flex items-center justify-center gap-2 py-10 text-slate1 text-xs">
              <Loader2 size={14} className="animate-spin" /> Carico i file 3D…
            </div>
          )}
          {!state.loading && filtered.length === 0 && (
            <div className="text-center py-8 text-sm text-ink/50">
              {state.files.length === 0 ? "Nessun file 3D disponibile per questo fornitore." : "Nessun file 3D corrisponde alla ricerca."}
            </div>
          )}
          {!state.loading && filtered.length > 0 && matchedCount > 0 && (
            <div className="text-[11px] text-ink/60 pb-1" data-testid="magic-3d-priority-hint">
              In cima {matchedCount} file che corrispondono a “{priorityTokens.join(" ")}”.
            </div>
          )}
          {!state.loading && filtered.map((f) => {
            const isPriority = priorityScore(f.name) > 0;
            return (
              <div
                key={f.key}
                className={`bg-white border rounded-sm p-3 flex items-center gap-3 ${isPriority ? "border-gold ring-1 ring-gold/30" : "border-ink/10"}`}
                data-testid={`magic-3d-item-${f.key}`}
              >
                <Model3DThumb file={f} isPriority={isPriority} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-ink truncate" title={f.name}>{f.name}</div>
                  {f.size > 0 && <div className="text-[11px] text-slate1">{(f.size / 1024 / 1024).toFixed(1)} MB</div>}
                </div>
                <button
                  onClick={() => download(f)}
                  className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 text-[10px] tracking-[0.15em] uppercase border border-ink/15 rounded-sm hover:border-gold hover:text-ink text-slate1 transition-colors"
                  data-testid={`magic-3d-download-${f.key}`}
                >
                  {f.source === "upload" ? <Download size={12} /> : <ExternalLink size={12} />} Scarica
                </button>
              </div>
            );
          })}
        </div>

        <div className="p-4 border-t border-ink/10 bg-white">
          {allowAsk ? (
            <>
              <p className="text-[11px] text-slate1 mb-2 leading-relaxed">
                Non trovi il modello che cercavi? Chiedilo direttamente al nostro team: la richiesta arriva in chat.
              </p>
              <button
                onClick={askModel}
                disabled={asking}
                className="w-full inline-flex items-center justify-center gap-2 bg-gold text-ink px-4 py-2.5 rounded-sm text-xs tracking-[0.2em] uppercase font-medium hover:bg-amber-400 disabled:opacity-50 transition-colors"
                data-testid="magic-3d-ask"
              >
                {asking ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Chiedi il modello che cercavi
              </button>
            </>
          ) : (
            <p className="text-[11px] text-slate1 leading-relaxed text-center">
              File 3D / CAD caricati per questo fornitore. Gestiscili dalla scheda fornitore in Admin.
            </p>
          )}
        </div>
      </div>
    </>
  );
}


// ───────────────────────────────────────────────────────────────────────────
// Drawer Preferiti: listini/pagine salvati (apri / rimuovi)
// ───────────────────────────────────────────────────────────────────────────
function FavThumb({ src, assetId, page }) {
  const [err, setErr] = useState(false);
  const [blobSrc, setBlobSrc] = useState("");
  useEffect(() => {
    if (src || !assetId || !page) return undefined;
    let alive = true;
    let url = "";
    api.get(`/assets/${assetId}/thumbnail`, { params: { page, width: 200 }, responseType: "blob", timeout: 25000 })
      .then(({ data }) => { if (!alive) return; url = URL.createObjectURL(data); setBlobSrc(url); })
      .catch(() => { if (alive) setErr(true); });
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [src, assetId, page]);
  const finalSrc = src || blobSrc;
  if (err || (!finalSrc && !(assetId && page))) {
    return (
      <div className="w-14 h-14 shrink-0 rounded-sm bg-cream2 border border-ink/10 flex items-center justify-center">
        <ImageIcon size={16} className="text-slate1/50" />
      </div>
    );
  }
  if (!finalSrc) {
    return (
      <div className="w-14 h-14 shrink-0 rounded-sm bg-cream2 border border-ink/10 flex items-center justify-center">
        <Loader2 size={14} className="text-slate1/40 animate-spin" />
      </div>
    );
  }
  return (
    <img
      src={finalSrc}
      alt=""
      referrerPolicy="no-referrer"
      loading="lazy"
      onError={() => setErr(true)}
      className="w-14 h-14 shrink-0 rounded-sm object-cover border border-ink/10 bg-cream2"
    />
  );
}

export function FavoritesDrawer({ favorites, onOpen, onRemove, onClose }) {
  return (
    <>
      <div className="fixed inset-0 z-[110] bg-black/40" onClick={onClose} data-testid="magic-fav-overlay" />
      <div className="fixed right-0 top-0 bottom-0 z-[111] w-[92vw] max-w-[460px] bg-cream shadow-2xl flex flex-col" data-testid="magic-fav-drawer">
        <div className="flex items-center justify-between px-5 py-4 bg-ink text-cream">
          <div className="flex items-center gap-2">
            <Star size={18} className="text-gold fill-gold" />
            <div>
              <div className="text-sm font-semibold tracking-wide">Preferiti</div>
              <div className="text-[10px] text-cream/60">{favorites.length} salvati</div>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-cream/10 rounded" data-testid="magic-fav-close"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="magic-fav-list">
          {favorites.length === 0 && (
            <div className="text-center py-10 text-sm text-ink/50">
              Nessun preferito. Tocca la <Star size={12} className="inline -mt-0.5 text-gold" /> su un risultato per salvarlo qui.
            </div>
          )}
          {favorites.map((f) => (
            <div key={f.id} className="bg-white border border-ink/10 rounded-sm p-3 flex items-start gap-3" data-testid={`magic-fav-item-${f.id}`}>
              <button onClick={() => onOpen(f)} className="min-w-0 flex-1 text-left group flex items-start gap-3" data-testid={`magic-fav-open-${f.id}`}>
                <FavThumb src={f.thumbnail} assetId={f.asset_id} page={f.page} />
                <div className="min-w-0 flex-1">
                  {f.query ? (
                    <div className="flex items-center gap-1 text-sm font-semibold text-ink leading-snug group-hover:text-gold transition-colors" data-testid={`magic-fav-query-${f.id}`}>
                      <Search size={12} className="shrink-0 text-gold" />
                      <span className="truncate capitalize">{f.query}</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 text-sm font-semibold text-ink leading-snug group-hover:text-gold transition-colors" data-testid={`magic-fav-page-${f.id}`}>
                      <FileText size={12} className="shrink-0 text-gold" />
                      <span className="truncate">Pagina {f.page} salvata</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-[9px] tracking-[0.2em] uppercase text-slate1 mt-1 flex-wrap">
                    <Building2 size={10} className="shrink-0" />
                    <span className="truncate">{f.supplier_name || "—"}</span>
                    {f.kind && <><span className="text-ink/30">·</span><span className="text-gold">{f.kind}</span></>}
                    <span className="text-ink/30">·</span><span className="font-medium">Pag. {f.page}</span>
                  </div>
                  <div className="text-xs text-slate1 leading-snug mt-1 flex items-center gap-1 group-hover:text-ink transition-colors">
                    <FileText size={11} className="shrink-0" /> <span className="truncate">{f.title || "Listino"}</span>
                  </div>
                </div>
              </button>
              <button
                onClick={() => onRemove(f)}
                className="shrink-0 p-2 text-slate1/60 hover:text-rose-600 transition-colors"
                title="Rimuovi dai preferiti"
                data-testid={`magic-fav-remove-${f.id}`}
              >
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
